import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Archive, Loader2, Tags } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { EmptyState, LoadingState } from "@/components/states";
import { IconPicker } from "@/components/appearance/IconPicker";
import { ColorSwatchPicker } from "@/components/appearance/ColorSwatchPicker";
import { AppIcon } from "@/lib/appearanceIcons";
import { chipStyle } from "@/lib/appearancePalette";
import { cn } from "@/lib/utils";

function CategoryChip({ c }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[13px] font-medium", !c.color && "border-transparent bg-muted text-muted-foreground")}
      style={chipStyle(c.color)} data-testid={`cat-chip-${c.id}`}>
      {c.icon ? <AppIcon name={c.icon} className="h-3.5 w-3.5" /> : <span className="h-2.5 w-2.5 rounded-full" style={c.color ? { backgroundColor: c.color } : undefined} />}
      {c.name}
    </span>
  );
}

export default function TaskCategories() {
  const perms = usePermissions();
  const canOrg = perms.is_owner || perms.has("Manage Task Categories");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {id?, name, color, icon, scope}

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/tasks/categories").then(setCats).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEditCat = (c) => (c.scope === "organisation" ? canOrg : true);
  const startNew = () => setEditing({ name: "", color: "", icon: "", scope: canOrg ? "organisation" : "personal" });
  const save = async () => {
    const body = { name: editing.name.trim(), color: editing.color || "", icon: editing.icon || "", scope: editing.scope };
    if (!body.name) return toast.error("Please name the category.");
    try {
      if (editing.id) await apiFetch(`/tasks/categories/${editing.id}`, { method: "PUT", body: JSON.stringify(body) });
      else await apiFetch("/tasks/categories", { method: "POST", body: JSON.stringify(body) });
      toast.success(editing.id ? "Category updated" : "Category created");
      setEditing(null); load();
    } catch (e) { toast.error("Could not save category", { description: e.message }); }
  };
  const archive = async (c) => {
    try { await apiFetch(`/tasks/categories/${c.id}/archive`, { method: "PUT", body: JSON.stringify({ archived: true }) }); toast.success("Category archived"); load(); }
    catch (e) { toast.error("Could not archive", { description: e.message }); }
  };

  return (
    <div data-testid="settings-task-categories">
      <PageHeader eyebrow="Appearance" title="Task Categories"
        subtitle="Give categories a colour and icon so tasks are scannable across every view."
        actions={<Button size="sm" className="gap-1.5" onClick={startNew} data-testid="cat-new"><Plus className="h-4 w-4" /> New category</Button>} />

      {loading ? <LoadingState label="Loading categories…" />
        : cats.length === 0 ? (
          <EmptyState title="No task categories have been created yet."
            message="Categories are optional. Create one when you want to group and colour-code tasks."
            action={<Button size="sm" className="gap-1.5" onClick={startNew} data-testid="cat-empty-new"><Plus className="h-4 w-4" /> New category</Button>} />
        ) : (
          <div className="grid gap-2" data-testid="cat-list">
            {cats.map((c) => (
              <Card key={c.id} className="flex items-center justify-between gap-3 p-3" data-testid={`cat-row-${c.id}`}>
                <div className="flex items-center gap-3">
                  <CategoryChip c={c} />
                  <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{c.scope}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" disabled={!canEditCat(c)} onClick={() => setEditing({ id: c.id, name: c.name, color: c.color || "", icon: c.icon || "", scope: c.scope })} data-testid={`cat-edit-${c.id}`} className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button variant="ghost" size="sm" disabled={!canEditCat(c)} onClick={() => archive(c)} data-testid={`cat-archive-${c.id}`} className="text-muted-foreground"><Archive className="h-3.5 w-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="cat-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing?.id ? "Edit category" : "New category"}</DialogTitle>
            <DialogDescription>Give the category a name, colour and optional icon so tasks are easy to scan.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5">
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Preview</div>
                <CategoryChip c={{ id: "preview", name: editing.name || "Category", color: editing.color, icon: editing.icon }} />
              </div>
              <div><Label className="text-xs">Name</Label><Input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Reels" data-testid="cat-name" /></div>
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={editing.scope} onValueChange={(v) => setEditing((p) => ({ ...p, scope: v }))} disabled={!!editing.id}>
                  <SelectTrigger data-testid="cat-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Personal (only you)</SelectItem>
                    {canOrg && <SelectItem value="organisation">Organisation (everyone)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><div className="mb-1.5 text-xs font-medium text-muted-foreground">Colour</div><ColorSwatchPicker value={editing.color} onChange={(v) => setEditing((p) => ({ ...p, color: v }))} /></div>
              <div><div className="mb-1.5 text-xs font-medium text-muted-foreground">Icon</div><IconPicker value={editing.icon} onChange={(v) => setEditing((p) => ({ ...p, icon: v }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="cat-cancel">Cancel</Button>
            <Button onClick={save} data-testid="cat-save">{editing?.id ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
